import { useEffect, useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLoginMutation, useLoginVerifyMutation, useDeleteInvestigationMutation } from '@/store/api/apiSlice';
import { useAppDispatch, useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser, setCredentials } from '@/store/slices/authSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

// Phrase the admin must type verbatim to arm the delete.
const CONFIRM_PHRASE = 'delete investigation';

interface DeleteInvestigationModalProps {
    isOpen: boolean;
    investigationId: string;
    employeeName: string;
    onClose: () => void;
    // Called after a successful delete so the caller can navigate / refetch.
    onDeleted: () => void;
}

/**
 * Two-factor confirmation before permanently deleting an investigation:
 *   1. Type the exact phrase "delete investigation".
 *   2. Verify a one-time code sent to the admin's email (the app authenticates
 *      via OTP, not passwords — this re-checks the admin's identity).
 *
 * On OTP verify we re-store the rotated tokens so the current session stays valid.
 */
export default function DeleteInvestigationModal({
    isOpen,
    investigationId,
    employeeName,
    onClose,
    onDeleted,
}: DeleteInvestigationModalProps) {
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector(selectCurrentUser);

    const [sendCode, { isLoading: isSendingCode }] = useLoginMutation();
    const [verifyCode] = useLoginVerifyMutation();
    const [deleteInvestigation] = useDeleteInvestigationMutation();

    const [phrase, setPhrase] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [otp, setOtp] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const [error, setError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Reset everything whenever the modal opens for a fresh target.
    useEffect(() => {
        if (isOpen) {
            setPhrase('');
            setCodeSent(false);
            setOtp('');
            setResendTimer(0);
            setError('');
            setIsDeleting(false);
        }
    }, [isOpen, investigationId]);

    // Resend countdown.
    useEffect(() => {
        if (resendTimer <= 0) return;
        const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
        return () => clearTimeout(t);
    }, [resendTimer]);

    if (!isOpen) return null;

    const phraseOk = phrase.trim().toLowerCase() === CONFIRM_PHRASE;
    const otpOk = otp.trim().length === 6;
    const busy = isSendingCode || isDeleting;

    const channel = currentUser?.email
        ? { email: currentUser.email }
        : currentUser?.phoneNumber
          ? { phoneNumber: currentUser.phoneNumber }
          : null;

    const maskedTarget = currentUser?.email
        ? currentUser.email.replace(/^(.{2}).*(@.*)$/, '$1•••$2')
        : currentUser?.phoneNumber || 'your account';

    const handleSendCode = async () => {
        if (!channel) {
            setError('No email or phone on your account to send a code to.');
            return;
        }
        setError('');
        try {
            await sendCode(channel).unwrap();
            setCodeSent(true);
            setResendTimer(30);
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not send the confirmation code.'));
        }
    };

    const handleDelete = async () => {
        if (!phraseOk || !otpOk || !channel) return;
        setError('');
        setIsDeleting(true);
        try {
            // 1. Verify identity via OTP. Re-store rotated tokens so we stay logged in.
            const verified = await verifyCode({ ...channel, otp: otp.trim() }).unwrap();
            if (verified?.access_token) {
                localStorage.setItem('access_token', verified.access_token);
                if (verified.refresh_token) {
                    localStorage.setItem('refresh_token', verified.refresh_token);
                }
                if (verified.user) {
                    dispatch(setCredentials({ user: verified.user, token: verified.access_token }));
                }
            }

            // 2. Delete.
            await deleteInvestigation(investigationId).unwrap();
            onDeleted();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not delete. Check the code and try again.'));
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !busy && onClose()} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
                <div className="p-5">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <Trash2 className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-bold text-gray-900">Delete investigation</h2>
                            <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                                This permanently deletes <span className="font-medium">{employeeName}</span>'s
                                investigation and all of its data. This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    {/* Step 1 — type the phrase */}
                    <div className="mt-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Type <span className="font-bold text-gray-900">{CONFIRM_PHRASE}</span> to confirm
                        </label>
                        <input
                            type="text"
                            autoComplete="off"
                            value={phrase}
                            onChange={(e) => setPhrase(e.target.value)}
                            placeholder={CONFIRM_PHRASE}
                            disabled={busy}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-transparent disabled:bg-gray-50"
                        />
                    </div>

                    {/* Step 2 — OTP */}
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmation code</label>
                        {!codeSent ? (
                            <Button
                                onClick={handleSendCode}
                                disabled={!phraseOk || isSendingCode}
                                className="w-full py-2.5 rounded-lg text-sm bg-gray-900 hover:bg-gray-800 text-white flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {isSendingCode && <Loader2 className="w-4 h-4 animate-spin" />}
                                Send code to {maskedTarget}
                            </Button>
                        ) : (
                            <>
                                <p className="text-xs text-gray-500 mb-2">
                                    We sent a 6-digit code to <span className="font-medium">{maskedTarget}</span>.
                                </p>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                    placeholder="Enter 6-digit code"
                                    disabled={busy}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-transparent disabled:bg-gray-50"
                                />
                                <button
                                    type="button"
                                    onClick={handleSendCode}
                                    disabled={resendTimer > 0 || isSendingCode}
                                    className="mt-2 text-xs font-medium text-gray-700 underline-offset-2 hover:underline disabled:text-gray-400 disabled:no-underline"
                                >
                                    {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                                </button>
                            </>
                        )}
                    </div>

                    {error && <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

                    <div className="mt-6 flex items-center gap-3">
                        <Button
                            onClick={onClose}
                            disabled={busy}
                            className="flex-1 py-2.5 rounded-lg text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            disabled={!phraseOk || !otpOk || isDeleting}
                            className="flex-1 py-2.5 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
